import { AppConfig, ProductionTypes } from '@shared/utils/app-config';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { CacheEvidencesInitiator, CacheService, CacheUsersInitiator } from '@shared/utils/cache';
import { BaseAuthService } from '@shared/utils/base-auth';
import { Subject, switchMap, take, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { AppRoutes } from '@shared/utils/navigation';

@Component({
    selector: 'feature-page-layout-layout-one-wrapper',
    templateUrl: './layout-wrapper.component.html',
    styleUrls: ['./layout-wrapper.component.css'],
    standalone: false
})
export class LayoutWrapperComponent implements OnInit, OnDestroy {
  public readonly productionTypes = ProductionTypes;
  private userUid = '';
  private listUsersStoreIdentifier = '';
  private listEvidencesStoreIdentifier = '';
  private ngUnsubscribe = new Subject<void>();

  constructor(private appConfig: AppConfig, private cacheService: CacheService, private baseAuthService: BaseAuthService, private router: Router) {}

  ngOnInit(): void {
    this.baseAuthService
      .getCurrentBaseUser()
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe((x) => {
        this.userUid = x?.uid ?? '';
        this.listUsersStoreIdentifier = `ListUsers-${this.userUid}`;
        this.listEvidencesStoreIdentifier = `ListEvidence-${this.userUid}`;
      });

    this.cacheService
      .cacheUsers(this.userUid, CacheUsersInitiator.InitialCache)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {});

    this.cacheService
      .cacheEvidences(this.userUid, CacheEvidencesInitiator.InitialCache)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {});

    this.cacheService.usersDataModified$
      .pipe(
        switchMap((initiator) => {
          return this.cacheService.cacheUsers(this.userUid, initiator, this.listUsersStoreIdentifier);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {});

    this.cacheService.evidencesDataModified$
      .pipe(
        switchMap((initiator) => {
          return this.cacheService.cacheEvidences(this.userUid, initiator);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {});

    this.cacheService.autoClearFiltersUpdated$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((autoClearFilters) => {
      if (autoClearFilters) {
        this.cacheService.storeUserDefaultQueryLocalStorage(this.userUid);
        this.cacheService.storeEvidenceDefaultQueryLocalStorage(this.userUid);

        this.cacheService.clearFiltersFromLocalStorage(this.listUsersStoreIdentifier);
        this.cacheService.clearFiltersFromLocalStorage(this.listEvidencesStoreIdentifier);

        if (this.router.url === `/${AppRoutes.Users}`) {
          this.cacheService.evidencesDataModified(CacheEvidencesInitiator.AutoClearFiltersUpdated);
        } else if (this.router.url === `/${AppRoutes.Files}`) {
          this.cacheService.usersDataModified(CacheUsersInitiator.AutoClearFiltersUpdated);
        } else {
          this.cacheService.usersDataModified(CacheUsersInitiator.AutoClearFiltersUpdated);
          this.cacheService.evidencesDataModified(CacheEvidencesInitiator.AutoClearFiltersUpdated);
        }
      }
    });
  }

  getProductionType(): ProductionTypes {
    return this.appConfig.productionType;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
